import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { PARAMETERS } from '@root/tracking/constants';
import actions from '@store/actions/listingPage';
import Metric from '@components/shared/Metric';
import type { Building } from '@root/types';
import ExpandableDescription from '@components/shared/ExpandableDescription';
import CustomIcon from '@components/shared/CustomIcon';
import NotableTenants from '../NotableTenants';
import SpaceProviderInfo from './SpaceProviderInfo';
import s from './BasicInfo.module.less';

type Props = {
  building: Building;
};

const BasicInfo = ({ building }: Props) => {
  const { t } = useTranslation('building');
  const dispatch = useDispatch();
  const {
    numberOfFloors,
    rentableBuildingArea,
    yearRenovated,
    yearBuilt,
    owningCompany,
    brochure,
  } = building;

  const renderBasicInfoList = () => (
    <div>
      <ul className={s.infoContainer} data-testid="basic-building-info">
        {numberOfFloors ? (
          <li>
            <Metric label={t('details.floors')} value={numberOfFloors} />
          </li>
        ) : null}
        {rentableBuildingArea ? (
          <li>
            <Metric
              label={t('details.rentableBuildingArea')}
              value={t('units:area', { area: rentableBuildingArea })}
            />
          </li>
        ) : null}
        {yearRenovated ? (
          <li>
            <Metric value={yearRenovated} label={t('details.yearRenovated')} />
          </li>
        ) : null}
        {yearBuilt ? (
          <li>
            <Metric value={yearBuilt} label={t('details.yearBuilt')} />
          </li>
        ) : null}
      </ul>
    </div>
  );

  return (
    <div className={s.container}>
      {owningCompany ? <SpaceProviderInfo spaceProvider={owningCompany} /> : null}
      {renderBasicInfoList()}
      {building.description && (
        <div className={s.descriptionContainer}>
          <ExpandableDescription>{building.description}</ExpandableDescription>
        </div>
      )}
      {brochure && (
        <div className={s.brochure}>
          <CustomIcon type="download" className={s.attributeIcon} />
          <a
            href={brochure.url}
            onClick={() => {
              dispatch(actions.attachmentClicked(PARAMETERS.buildingPage, brochure));
            }}
          >
            {brochure.name}
          </a>
        </div>
      )}
      {isEmpty(building.notableTenants) ? null : (
        <NotableTenants notableTenants={building.notableTenants} />
      )}
    </div>
  );
};

export default BasicInfo;
